/**
 * Game Page Component
 * Where the actual dice game happens
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { gameSocket } from '../services/socket';
import { userAPI } from '../services/api';
import '../styles/Flappy.css';
import '../styles/Game.css';

const Game = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, updateUser } = useAuth();

  const [gameState, setGameState] = useState('waiting'); // waiting, ready, rolling, rolled, result
  const [myRoll, setMyRoll] = useState(null);
  const [opponentRoll, setOpponentRoll] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [opponentProfile, setOpponentProfile] = useState(null);
  const [opponentConnected, setOpponentConnected] = useState(false);

  const gameData = location.state || {};

  const payoutSummary = (() => {
    const wager = Number(gameData?.wager || 0);
    if (!wager) return null;
    const grossPot = wager * 2;
    const chargeAmount = Math.floor(grossPot * 0.07);
    const netPot = Math.max(0, grossPot - chargeAmount);
    return { grossPot, chargeAmount, netPot };
  })();

  useEffect(() => {
    console.log('Game page loaded - gameId:', gameId);
    
    // Tell backend we've joined the game
    gameSocket.joinGame(gameId);
    console.log('Emitted game:joined for gameId:', gameId);

    // Load opponent profile
    if (gameData.opponentId) {
      console.log('Loading opponent profile for:', gameData.opponentId);
      userAPI.getUserById(gameData.opponentId)
        .then((res) => {
          setOpponentProfile(res.data);
          console.log('Opponent profile loaded:', res.data.username);
        })
        .catch((err) => console.error('Failed to load opponent profile:', err));
    } else {
      console.log('No opponentId in gameData:', gameData);
    }

    // Listen for opponent joining
    gameSocket.onOpponentJoined(() => {
      console.log('✓ Opponent has joined the game - changing state to ready');
      setOpponentConnected(true);
      setGameState('ready');
    });

    // Listen for roll
    gameSocket.onRolled((data) => {
      console.log('Received rolled event:', data);
      setMyRoll(data.roll);
      setGameState('rolled');
    });

    // Listen for result
    gameSocket.onGameResult((data) => {
      console.log('Received game result:', data);
      setOpponentRoll(data.opponentRoll);
      setResult(data);
      setGameState('result');
      
      // Fetch updated user profile to get new balance and stats
      if (user && user.id) {
        userAPI.getUserById(user.id)
          .then((res) => {
            console.log('Updated user profile:', res.data);
            updateUser(res.data);
          })
          .catch((err) => console.error('Failed to update user profile:', err));
      }
    });

    return () => {
      gameSocket.removeGameListeners();
    };
  }, [gameId, gameData.opponentId]);


  const handleRoll = () => {
    setGameState('rolling');
    gameSocket.roll(gameId);
  };

  const handlePlayAgain = () => {
    navigate('/dashboard');
  };

  return (
    <div className="chess-match-page">
      {/* ── Top bar ── */}
      <div className="cm-topbar">
        <button className="cm-icon-btn" onClick={() => navigate('/dashboard')}>←</button>
        <div className="cm-topbar-center">
          <span className="cm-topbar-title">Dice Game</span>
          {gameData.wager > 0 && <span className="cm-badge cm-badge--gold">💰 {gameData.wager}</span>}
        </div>
        <span style={{ width: 36 }} />
      </div>

      {/* ── Player bars ── */}
      <div className={`cm-player-bar${gameState === 'result' ? '' : ' cm-player-bar--active'}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">🎲</span>
          <span className="cm-player-bar__name">{opponentProfile?.username || 'Opponent'}</span>
        </div>
        {opponentRoll != null && <div className="dice-roll-value">{opponentRoll}</div>}
      </div>

      {/* ── Dice area ── */}
      <div className="dice-arena">
        {gameState === 'waiting' && (
          <div className="dice-arena__status">
            <div className="dice-arena__spinner">🎲</div>
            <span>Waiting for opponent…</span>
          </div>
        )}

        {gameState === 'ready' && (
          <div className="dice-arena__status">
            <span style={{ color: '#86efac' }}>Opponent connected!</span>
            <button className="cm-btn cm-btn--primary dice-roll-btn" onClick={handleRoll}>Roll Dice</button>
          </div>
        )}

        {gameState === 'rolling' && (
          <div className="dice-arena__status">
            <div className="dice-arena__spinner">🎲</div>
            <span>Rolling…</span>
          </div>
        )}

        {gameState === 'rolled' && !result && (
          <div className="dice-arena__status">
            <div className="dice-roll-value dice-roll-value--big">{myRoll}</div>
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Waiting for opponent…</span>
          </div>
        )}

        {gameState === 'result' && result && (
          <div className="dice-arena__versus">
            <div className="dice-arena__col">
              <div className="dice-roll-value dice-roll-value--big">{result.yourRoll}</div>
              <span className="dice-arena__label">{result.yourUsername || user?.username}</span>
            </div>
            <span className="dice-arena__vs">VS</span>
            <div className="dice-arena__col">
              <div className="dice-roll-value dice-roll-value--big">{result.opponentRoll}</div>
              <span className="dice-arena__label">{result.opponentUsername || 'Opponent'}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── You bar ── */}
      <div className={`cm-player-bar${gameState !== 'result' ? ' cm-player-bar--active' : ''}`}>
        <div className="cm-player-bar__info">
          <span className="cm-player-bar__piece">🎲</span>
          <span className="cm-player-bar__name">{user?.username || 'You'}</span>
        </div>
        {myRoll != null && gameState !== 'result' && <div className="dice-roll-value">{myRoll}</div>}
      </div>

      {/* ── Result banner ── */}
      {gameState === 'result' && result && (() => {
        const isWin = result.winnerId === user?.id;
        const isTie = !result.winnerId;
        const cls = isTie ? 'cm-result-banner--draw' : isWin ? 'cm-result-banner--win' : 'cm-result-banner--loss';
        return (
          <div className={`cm-result-banner ${cls}`}>
            <span className="cm-result-banner__icon">{isTie ? '🤝' : isWin ? '🏆' : '😞'}</span>
            <span>{result.message}</span>
            {isWin && <span className="cm-result-banner__payout">+{payoutSummary ? payoutSummary.netPot : gameData.wager * 2} wp</span>}
            {!isTie && !isWin && <span className="cm-result-banner__payout">-{gameData.wager} wp</span>}
          </div>
        );
      })()}

      {payoutSummary && gameState === 'result' && (
        <div className="cm-status-line">
          Pot {payoutSummary.grossPot} · Charge {payoutSummary.chargeAmount} · Net {payoutSummary.netPot}
        </div>
      )}

      {/* ── Actions ── */}
      <div className="cm-actions">
        {(gameState === 'ready' || gameState === 'rolled') && !result && (
          <button className="cm-btn cm-btn--primary" onClick={handleRoll} disabled={gameState === 'rolling'}>
            {gameState === 'rolling' ? 'Rolling…' : 'Roll Dice'}
          </button>
        )}
        {gameState === 'result' && (
          <button className="cm-btn cm-btn--primary" onClick={handlePlayAgain}>Back to Dashboard</button>
        )}
      </div>

      {error && <div className="cm-toast cm-toast--error">{error}</div>}
    </div>
  );
};

export default Game;

